import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, parse, startOfYear, endOfYear, eachMonthOfInterval } from 'date-fns';
import { nl } from 'date-fns/locale';
import { TrendingUp, TrendingDown, Minus, ArrowLeftRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAccentColor } from '../lib/useAccentColor';
import Skeleton from './Skeleton';

interface PeriodData {
  total: number;
  cash: number;
  card: number;
  invoice: number;
  tips: number;
  daysWorked: number;
  avgPerDay: number;
}

type CompareMode = 'months' | 'quarters';

const QUARTERS = [
  { label: 'K1', months: [1, 2, 3] },
  { label: 'K2', months: [4, 5, 6] },
  { label: 'K3', months: [7, 8, 9] },
  { label: 'K4', months: [10, 11, 12] },
];

const MONTH_NAMES = [
  'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
  'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'
];

function DiffBadge({ diff, pct }: { diff: number; pct: number }) {
  if (Math.abs(diff) < 0.01) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs text-gray-400">
        <Minus className="h-3 w-3" /> €0.00
      </span>
    );
  }
  const positive = diff > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${positive ? 'text-emerald-400' : 'text-red-400'}`}>
      {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {positive ? '+' : ''}€{diff.toFixed(2)} ({positive ? '+' : ''}{pct.toFixed(1)}%)
    </span>
  );
}

export default function ComparisonView() {
  const { accentColor } = useAccentColor();
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const [mode, setMode] = useState<CompareMode>('months');
  const [activeYears, setActiveYears] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  const [leftYear, setLeftYear] = useState(currentYear);
  const [leftMonth, setLeftMonth] = useState(currentMonth > 1 ? currentMonth - 1 : 12);
  const [leftQuarter, setLeftQuarter] = useState(1);

  const [rightYear, setRightYear] = useState(currentYear);
  const [rightMonth, setRightMonth] = useState(currentMonth);
  const [rightQuarter, setRightQuarter] = useState(Math.min(2, 4));

  const [leftData, setLeftData] = useState<PeriodData | null>(null);
  const [rightData, setRightData] = useState<PeriodData | null>(null);
  const [compared, setCompared] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: settings } = await supabase
        .from('settings')
        .select('active_years')
        .eq('user_id', user.id)
        .maybeSingle();
      if (settings?.active_years) {
        setActiveYears(settings.active_years.sort((a: number, b: number) => a - b));
      } else {
        setActiveYears([currentYear]);
      }
    })();
  }, []);

  const fetchPeriodData = async (year: number, month: number | null, quarter: number | null): Promise<PeriodData> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { total: 0, cash: 0, card: 0, invoice: 0, tips: 0, daysWorked: 0, avgPerDay: 0 };

    let dateStart: string;
    let dateEnd: string;

    if (month !== null) {
      const d = parse(`${year}-${String(month).padStart(2, '0')}`, 'yyyy-MM', new Date());
      dateStart = format(startOfMonth(d), 'yyyy-MM-dd');
      dateEnd = format(endOfMonth(d), 'yyyy-MM-dd');
    } else {
      const q = QUARTERS[(quarter ?? 1) - 1];
      const firstMonth = parse(`${year}-${String(q.months[0]).padStart(2, '0')}`, 'yyyy-MM', new Date());
      const lastMonth = parse(`${year}-${String(q.months[2]).padStart(2, '0')}`, 'yyyy-MM', new Date());
      dateStart = format(startOfMonth(firstMonth), 'yyyy-MM-dd');
      dateEnd = format(endOfMonth(lastMonth), 'yyyy-MM-dd');
    }

    const [{ data: entries }, { data: tipsData }] = await Promise.all([
      supabase.from('revenue_entries').select('cash_revenue, card_revenue, invoice_revenue').eq('user_id', user.id).gte('date', dateStart).lte('date', dateEnd),
      supabase.from('hidden_revenue').select('amount').eq('user_id', user.id).gte('date', dateStart).lte('date', dateEnd),
    ]);

    const cash = (entries || []).reduce((s, e) => s + e.cash_revenue, 0);
    const card = (entries || []).reduce((s, e) => s + e.card_revenue, 0);
    const invoice = (entries || []).reduce((s, e) => s + e.invoice_revenue, 0);
    const tips = (tipsData || []).reduce((s, e) => s + e.amount, 0);
    const total = cash + card + invoice;
    const daysWorked = (entries || []).filter(e => e.cash_revenue + e.card_revenue + e.invoice_revenue > 0).length;

    return {
      total,
      cash,
      card,
      invoice,
      tips,
      daysWorked,
      avgPerDay: daysWorked > 0 ? total / daysWorked : 0,
    };
  };

  const handleCompare = async () => {
    setLoading(true);
    setCompared(false);
    const [l, r] = await Promise.all([
      fetchPeriodData(leftYear, mode === 'months' ? leftMonth : null, mode === 'quarters' ? leftQuarter : null),
      fetchPeriodData(rightYear, mode === 'months' ? rightMonth : null, mode === 'quarters' ? rightQuarter : null),
    ]);
    setLeftData(l);
    setRightData(r);
    setCompared(true);
    setLoading(false);
  };

  const getPeriodLabel = (year: number, month: number | null, quarter: number | null) => {
    if (month !== null) return `${MONTH_NAMES[month - 1]} ${year}`;
    return `K${quarter} ${year}`;
  };

  const diff = (l: number, r: number) => r - l;
  const pct = (l: number, r: number) => l === 0 ? 0 : ((r - l) / l) * 100;

  const years = activeYears.length > 0 ? activeYears : [currentYear];

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
        Vergelijken
      </h2>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6">
        {/* Mode toggle */}
        <div className="flex gap-2">
          {(['months', 'quarters'] as const).map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setCompared(false); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                mode === m
                  ? 'bg-accent text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {m === 'months' ? 'Maanden' : 'Kwartalen'}
            </button>
          ))}
        </div>

        {/* Period selectors */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left period */}
          <div className="space-y-3">
            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Periode A</p>
            <div className="flex gap-2">
              <select
                value={leftYear}
                onChange={(e) => setLeftYear(Number(e.target.value))}
                className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              {mode === 'months' ? (
                <select
                  value={leftMonth}
                  onChange={(e) => setLeftMonth(Number(e.target.value))}
                  className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  {MONTH_NAMES.map((name, i) => <option key={i + 1} value={i + 1}>{name}</option>)}
                </select>
              ) : (
                <select
                  value={leftQuarter}
                  onChange={(e) => setLeftQuarter(Number(e.target.value))}
                  className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  {[1, 2, 3, 4].map(q => <option key={q} value={q}>K{q}</option>)}
                </select>
              )}
            </div>
          </div>

          {/* Right period */}
          <div className="space-y-3">
            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Periode B</p>
            <div className="flex gap-2">
              <select
                value={rightYear}
                onChange={(e) => setRightYear(Number(e.target.value))}
                className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              {mode === 'months' ? (
                <select
                  value={rightMonth}
                  onChange={(e) => setRightMonth(Number(e.target.value))}
                  className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  {MONTH_NAMES.map((name, i) => <option key={i + 1} value={i + 1}>{name}</option>)}
                </select>
              ) : (
                <select
                  value={rightQuarter}
                  onChange={(e) => setRightQuarter(Number(e.target.value))}
                  className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  {[1, 2, 3, 4].map(q => <option key={q} value={q}>K{q}</option>)}
                </select>
              )}
            </div>
          </div>
        </div>

        <button
          onClick={handleCompare}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-sm font-medium text-white bg-accent hover:bg-accent-hover disabled:opacity-50 transition-colors"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
          ) : (
            <>
              <ArrowLeftRight className="h-4 w-4" />
              Vergelijken
            </>
          )}
        </button>
      </div>

      {/* Results */}
      {compared && leftData && rightData && (
        <div className="space-y-4">
          {/* Header labels */}
          <div className="grid grid-cols-3 gap-4 text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-1">
            <div>{getPeriodLabel(leftYear, mode === 'months' ? leftMonth : null, mode === 'quarters' ? leftQuarter : null)}</div>
            <div className="text-center">Verschil</div>
            <div className="text-right">{getPeriodLabel(rightYear, mode === 'months' ? rightMonth : null, mode === 'quarters' ? rightQuarter : null)}</div>
          </div>

          {/* Total card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Totale Omzet</span>
            </div>
            <div className="grid grid-cols-3 gap-4 items-center px-4 py-4">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">€{leftData.total.toFixed(2)}</div>
              <div className="text-center">
                <DiffBadge diff={diff(leftData.total, rightData.total)} pct={pct(leftData.total, rightData.total)} />
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white text-right">€{rightData.total.toFixed(2)}</div>
            </div>
          </div>

          {/* Breakdown rows */}
          {[
            { label: 'Contant', lVal: leftData.cash, rVal: rightData.cash },
            { label: 'Pin', lVal: leftData.card, rVal: rightData.card },
            { label: 'Rekening', lVal: leftData.invoice, rVal: rightData.invoice },
            { label: 'Fooi', lVal: leftData.tips, rVal: rightData.tips },
            { label: 'Werkdagen', lVal: leftData.daysWorked, rVal: rightData.daysWorked, isCount: true },
            { label: 'Gem. per dag', lVal: leftData.avgPerDay, rVal: rightData.avgPerDay },
          ].map(({ label, lVal, rVal, isCount }) => (
            <div key={label} className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</span>
              </div>
              <div className="grid grid-cols-3 gap-4 items-center px-4 py-3">
                <div className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                  {isCount ? lVal : `€${lVal.toFixed(2)}`}
                </div>
                <div className="text-center">
                  {isCount ? (
                    <span className={`text-xs font-medium ${rVal - lVal > 0 ? 'text-emerald-400' : rVal - lVal < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                      {rVal - lVal > 0 ? '+' : ''}{rVal - lVal}
                    </span>
                  ) : (
                    <DiffBadge diff={diff(lVal, rVal)} pct={pct(lVal, rVal)} />
                  )}
                </div>
                <div className="text-lg font-semibold text-gray-800 dark:text-gray-100 text-right">
                  {isCount ? rVal : `€${rVal.toFixed(2)}`}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
